/**
 * Bridge Offline Banner Component
 *
 * Displays a prominent warning banner when the bridge connection is unavailable.
 * Informs patients that Bridge-backed services are temporarily unavailable.
 *
 * @since 1.0.0
 */

import { AlertTriangle, Siren } from 'lucide-react'
import type { ReactNode } from 'react'
import { useTranslation } from 'react-i18next'
import { useSizing } from '../../../lib/use-sizing'
import { Body } from '../../../components/typography/body'
import { Small } from '../../../components/typography/small'

/**
 * Offline status banner displayed when bridge is unreachable.
 *
 * @example
 * ```tsx
 * function SelfServicePage() {
 *   const { isConnected } = useBridgeConnectivity()
 *
 *   return (
 *     <>
 *       {!isConnected && <BridgeOfflineBanner />}
 *       <MainContent />
 *     </>
 *   )
 * }
 * ```
 */
function OfflineBannerContent({
  localEmergencyNotice,
}: {
  readonly localEmergencyNotice?: ReactNode;
}) {
  const { t } = useTranslation()
  const sizing = useSizing()

  return (
    <output
      data-testid="bridge-offline-banner"
      aria-live="polite"
      className="w-full bg-destructive/10 border-b-2 border-destructive/30 px-6 py-4"
    >
      <span className="max-w-2xl mx-auto flex flex-col gap-2">
        {/* Title with warning icon */}
        <span className="flex items-center gap-3">
          <AlertTriangle className="size-6 text-destructive flex-shrink-0" />
          <Body as="span" size={sizing.body} weight="semibold" className="text-destructive">
            {t('self-service.offline.banner-title')}
          </Body>
        </span>

        {/* Message */}
        <span className="pl-9">
          <Small as="span" size={sizing.small} className="text-destructive/80">
            {t('self-service.offline.banner-message')}
          </Small>
        </span>

        {localEmergencyNotice}
      </span>
    </output>
  )
}

/**
 * Offline banner for a kiosk without a locally authorized emergency alert.
 *
 * @since 1.2.0
 * @example
 * ```tsx
 * <BridgeOfflineBanner />
 * ```
 */
export function BridgeOfflineBanner() {
  return <OfflineBannerContent />
}

/**
 * Offline banner that truthfully names the still-authorized local alert.
 *
 * @since 1.2.0
 * @example
 * ```tsx
 * <BridgeOfflineBannerWithLocalEmergency />
 * ```
 */
export function BridgeOfflineBannerWithLocalEmergency() {
  const { t } = useTranslation()
  const sizing = useSizing()

  return (
    <OfflineBannerContent
      localEmergencyNotice={
        <span
          data-testid="bridge-offline-local-emergency"
          className="pl-9 flex items-center gap-2 mt-1"
        >
          <Siren className="size-4 text-red-600 flex-shrink-0" />
          <Small as="span" size={sizing.small} className="text-red-700">
            {t('self-service.offline.emergency-available')}
          </Small>
        </span>
      }
    />
  )
}
